import { Component, EventListener, findComponents, GondelBaseComponent, GondelComponent } from '@gondel/core';
import { load } from 'recaptcha-v3';
import { IValidatableInputComponent } from '../../../../shared/utils/js/validatable-input.interface';
import sanitizeHtml from 'sanitize-html';

/**
 * lead-form module implementation.
 * @author sturudic <srdan.turudic@emea.merkleinc.com>
 */

enum Selectors {
	Button = '.js-cms-lead-form__submit-button',
	Loader = '.js-cms-lead-form__loader',
	Form = '.cms-lead-form__form',
	SuccessResponseContainer = '.cms-lead-form__success-response-container',
	ErrorResponseContainer = '.cms-lead-form__errors',
	ErrorResponseText = '.cms-lead-form__errors__text',
}

enum States {
	/**
	 * 	Form sending state
	 */
	Pending = 'state-cms-lead-form__loader--visible',
	ErrorVisible = 'state-cms-lead-form__errors--visible',
}

enum CustomDataAttributes {
	Url = 'data-api',
	FormId = 'data-id',
	SiteKey = 'data-sitekey',
}

@Component('LeadForm')
export class LeadForm extends GondelBaseComponent {
	private _api: string = '';
	private _loader: HTMLDivElement = {} as HTMLDivElement;
	private _form: HTMLDivElement = {} as HTMLDivElement;
	private _components: GondelComponent<HTMLElement>[] = [];

	public start() {
		this._api = this._ctx.getAttribute(CustomDataAttributes.Url) as string;

		const wrap = this._ctx.querySelector('.cms-lead-form__wrap') as HTMLElement;
		this._loader = this._ctx.querySelector(Selectors.Loader) as HTMLDivElement;
		this._form = this._ctx.querySelector(Selectors.Form) as HTMLDivElement;

		this._components = [
			...findComponents(wrap, 'CheckboxGroup'),
			...findComponents(wrap, 'Input'),
			// ...findComponents(wrap, 'Checkbox'),
			...findComponents(wrap, 'Textarea'),
		];
	}

	 
	@EventListener('click', Selectors.Button)
	public _onSubmitClick(e) {
		e.preventDefault();
		e.stopImmediatePropagation();

		if (!this._api) {
			throw new Error('API for submit not defined.');
		}

		const valid = this._components.reduce(
			((isValid: boolean, current: IValidatableInputComponent) => {
				const currentIsValid = current._isValid();
				return isValid ? currentIsValid : isValid;
			}) as any
			,true
		);

		if (valid) {
			const bindingModel = this._buildBindingModel();

			this._postForm(bindingModel);
		}
	}

	/**
	 * Gets reCAPTCHA token using the recaptcha-v3 load function with the sitekey as a parameter
	 * @private
	 */
	private async _getRecaptchaToken(recaptchaSiteKey) {
		const recaptcha = await load(recaptchaSiteKey);
		const token = await recaptcha.execute('submit');
		return token;
	}

	private async _postForm(bindingModel: any) {
		this._loader.classList.add(States.Pending);

		// get reCAPTCHA token using siteKey
		const recaptchaSiteKey = this._form.getAttribute(CustomDataAttributes.SiteKey);
		bindingModel.recaptchaToken = await this._getRecaptchaToken(recaptchaSiteKey);

		fetch(this._api, {
			method: 'POST',
			cache: 'no-cache',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(bindingModel),
		})
			.then(async (data: Response) => {
				const parsed = await data.json();

				if (data.status > 399) {
					this._handleErrors(parsed);
				} else {
					this._handleSuccess(parsed);
				}
			})
			 
			.catch((error) => console.log(error))
			.finally(() => this._loader.classList.remove(States.Pending));
	}

	/**
	 * Extracts the data from the form and builds it into a new object
	 * @private
	 */
	private _buildBindingModel(): any {
		const bindingModel = {
			id: this._ctx.getAttribute(CustomDataAttributes.FormId),
		};

		this._components.forEach((component: IValidatableInputComponent | any) => {
			const data = component.getData();
			if (data) {
				bindingModel[data.name] = data.value;
			}
		});

		return bindingModel;
	}

	private _handleSuccess(response: any): void {
		const message = response.message;
		const el = this._ctx.querySelector(Selectors.SuccessResponseContainer) as HTMLDivElement;
		el.innerHTML = sanitizeHtml(message);
		this._ctx.removeChild(this._ctx.querySelector(Selectors.Form) as Element);
	}

	private _handleErrors(e): void {
		if (!e.sent) {
			(this._ctx.querySelector(Selectors.ErrorResponseText) as HTMLParagraphElement).innerHTML = sanitizeHtml(e.message);
			this._ctx.querySelector(Selectors.ErrorResponseContainer)?.classList.add(States.ErrorVisible);

			if (e.validation_errors) {
				const keys = Object.keys(e.validation_errors);
				keys.forEach((key: string) => {
					const field = this._components.find(
						(x) =>
							(x._ctx.hasAttribute('name') || x._ctx.hasAttribute('data-name')) &&
							(x._ctx.getAttribute('name') === key || x._ctx.getAttribute('data-name') === key)
					) as IValidatableInputComponent;

					field._showValidationErrorsByBe(e.validation_errors[key]);
				});
			}
		}
	}
}
